import React, { useEffect, useRef, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { CancelButton, SubmitButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import {
  createWebinars,
  updateWebinars,
  uploadWebinarPoster,
} from '../../../redux/features/webinarSlice';
import { resolveWebinarPoster } from './resolveWebinarPoster';
import ActiveConflictAlert, {
  isActiveConflict,
  activeConflictName,
} from './ActiveConflictAlert';

// Upload limit enforced client-side to fail fast (backend caps at 4 MB too).
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

// The API stores calendar days, not timestamps.
const API_DATE_FORMAT = 'YYYY-MM-DD';

// Add / Edit a single webinar. `row` present -> edit mode.
//
// NEITHER MODE CARRIES AN ACTIVE/INACTIVE TOGGLE. Activation lives in exactly
// one place — the Status column in the list — because only one webinar may be
// active at a time, so a toggle in here is a control that fails at save time
// roughly whenever a campaign is already running, and it fails only after the
// user has filled in a whole form. A new webinar is always created Inactive,
// and an edit leaves the existing status exactly as it found it: the update
// payload OMITS is_active, and the server then preserves the stored value (see
// buildWebinarPayload in webinar.controller.js). So this dialog cannot change
// status even by accident.
//
// THE POSTER PREVIEWS THE INSTANT A FILE IS PICKED, from a local object URL,
// without waiting for the upload round trip and without depending on
// REACT_APP_UPLOADS_URL being right. In edit mode the row's existing poster
// shows on open, and picking a new file replaces it immediately.
const WebinarAddEdit = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const { mutating, uploading } = useSelector((state) => state.webinar);
  const handleApiResponse = useHandleApiResponse();
  const { enqueueSnackbar } = useSnackbar();

  // The "two active webinars" refusal from the API, held so the alert box can
  // repeat the server's own wording rather than inventing its own.
  //
  // KEPT EVEN THOUGH THIS DIALOG NO LONGER TOGGLES STATUS. It is not reachable
  // in normal use any more, but it is a cheap safety net: if the table ever ends
  // up with two active rows and one is repaired by hand, the server can still
  // answer 409 here, and this turns that into the same alert box the list shows
  // rather than a bare error snackbar.
  const [conflict, setConflict] = useState({ open: false, message: '', name: null });

  /*
    The picked file rendered straight from the browser, shown the moment the
    file dialog closes — before the upload has even started.

    This is what makes the preview immediate. The alternative, waiting for the
    upload and then pointing an <img> at the returned filename, means a visible
    gap on every pick and leaves the preview at the mercy of
    REACT_APP_UPLOADS_URL, which is exactly what was broken here.

    Held alongside `formik.values.poster` rather than replacing it: the FORM
    still submits the server's filename, this is display only.
  */
  const [localPreview, setLocalPreview] = useState(null);

  /*
    The object URL currently held in `localPreview`.

    A ref as well as state because it has to be revoked from places that must
    not re-run on every render — the unmount cleanup below, and the next pick.
    Every createObjectURL pins its blob in memory until revoked, so a user
    swapping the poster a few times would otherwise leak each image.
  */
  const previewUrl = useRef(null);

  const clearLocalPreview = () => {
    if (previewUrl.current) {
      URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = null;
    }
    setLocalPreview(null);
  };

  // Revoke on unmount, i.e. when the dialog closes by any route — submit,
  // cancel, the X, or a click outside.
  useEffect(
    () => () => {
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    },
    [],
  );

  const initialValues = {
    webinar_name: row?.webinar_name ?? '',
    from_date: row?.from_date ? dayjs(row.from_date) : null,
    to_date: row?.to_date ? dayjs(row.to_date) : null,
    poster: row?.poster ?? '',
    // NO is_active HERE. Nothing in this form is bound to it any more, and a
    // value the form cannot change has no business being in its state where a
    // future edit might start sending it again by accident.
  };

  const schema = Yup.object({
    webinar_name: Yup.string().trim().required('Webinar name is required'),
    from_date: Yup.date().typeError('From date must be a valid date').required('From date is required'),
    to_date: Yup.date()
      .typeError('To date must be a valid date')
      .required('To date is required')
      .min(Yup.ref('from_date'), 'To date cannot be earlier than from date'),
    poster: Yup.string().nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const payload = {
        webinar_name: values.webinar_name.trim(),
        from_date: dayjs(values.from_date).format(API_DATE_FORMAT),
        to_date: dayjs(values.to_date).format(API_DATE_FORMAT),
        // Send null (not "") so an emptied poster clears server-side.
        poster: values.poster?.trim() ? values.poster.trim() : null,
      };

      let res;
      if (row) {
        /*
          UPDATE — webinar_id goes in the body (coerced to number inside the
          slice).

          is_active IS DELIBERATELY ABSENT from this payload. The API treats an
          omitted is_active as "leave it alone" and keeps the stored value, so
          editing a webinar's name or dates can never disturb which one is
          active. Sending the row's current value instead would look equivalent
          but is not: it would re-assert a status from a snapshot taken when the
          dialog opened, and quietly undo a toggle made in the list in between.
        */
        res = await dispatch(updateWebinars({ webinar_id: row.webinar_id, ...payload }));
      } else {
        /*
          CREATE — slice wraps this into { data: [ ... ] }.

          Always Inactive, stated explicitly rather than left to the server's
          default, so the intent survives a change of default.
        */
        res = await dispatch(createWebinars({ ...payload, is_active: false }));
      }

      // Refused because another webinar is already active — an alert box, not a
      // snackbar, and the dialog stays open so the values are not lost.
      if (isActiveConflict(res)) {
        setConflict({
          open: true,
          message: res?.payload?.message,
          name: activeConflictName(res),
        });
        return;
      }

      handleApiResponse(res, {
        onSuccess: () => {
          formik.resetForm();
          if (onClose) onClose(true); // refetch the table
        },
      });
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    if (onClose) onClose(false);
  };

  /*
    Show the picked file at once, then upload it and keep the returned bare
    filename as `poster` — that filename, not the preview, is what gets saved.

    ORDER MATTERS HERE. The preview is set BEFORE the await, so it paints on the
    same tick the file dialog closes. Doing it after the upload resolved is what
    made the poster look like it had not registered at all: on a slow connection
    that is a second or more of a form that appears to have ignored the pick.
  */
  const handlePosterUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = null; // allow re-picking the same file
    if (!file) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      enqueueSnackbar('File is too large. Maximum size is 4 MB.', { variant: 'error' });
      return;
    }

    // Drop the previous object URL before minting the next one, or swapping the
    // poster repeatedly pins every earlier blob in memory for the page's life.
    clearLocalPreview();
    const objectUrl = URL.createObjectURL(file);
    previewUrl.current = objectUrl;
    setLocalPreview(objectUrl);

    const res = await dispatch(uploadWebinarPoster(file));
    const filename = res?.payload?.data?.filename;
    if (res?.payload?.status === 'success' && filename) {
      formik.setFieldValue('poster', filename);
      /*
        The local preview deliberately STAYS after a successful upload. It is
        byte-for-byte the file the server just stored, it is already decoded and
        on screen, and keeping it avoids both a re-fetch and the flash that
        swapping the src would cause. It is revoked when the dialog unmounts.
      */
    } else {
      /*
        Upload failed — take the preview away again. Leaving it up would show a
        poster that is not attached to anything and that saving would not
        persist, which is worse than showing no preview at all.
      */
      clearLocalPreview();
      enqueueSnackbar(res?.payload?.message || 'Poster upload failed', { variant: 'error' });
    }
  };

  const removePoster = () => {
    clearLocalPreview();
    formik.setFieldValue('poster', '');
  };

  /*
    What the preview <img> points at, in priority order:

      1. the just-picked file, straight from the browser
      2. the API's absolute poster_url, but ONLY while the form still holds the
         row's original filename — once a new file is uploaded that URL points
         at the OLD poster and would show a stale image
      3. a URL built from REACT_APP_UPLOADS_URL, for a filename with no row
         behind it (e.g. this dialog reopened after an upload)
  */
  const posterSrc =
    localPreview ||
    resolveWebinarPoster(
      formik.values.poster,
      row?.poster && row.poster === formik.values.poster ? row.poster_url : null,
    );

  // True while a poster is picked OR uploading, so the box shows the image
  // rather than the "No poster" placeholder during the round trip.
  const hasPoster = Boolean(posterSrc);

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            id="webinar_name"
            name="webinar_name"
            label="Webinar Name"
            value={formik.values.webinar_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.webinar_name && Boolean(formik.errors.webinar_name)}
            helperText={formik.touched.webinar_name && formik.errors.webinar_name}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={formik.values.from_date}
                onChange={(date) => formik.setFieldValue('from_date', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: formik.touched.from_date && Boolean(formik.errors.from_date),
                    helperText: formik.touched.from_date && formik.errors.from_date,
                  },
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={formik.values.to_date}
                minDate={formik.values.from_date || undefined}
                onChange={(date) => formik.setFieldValue('to_date', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: formik.touched.to_date && Boolean(formik.errors.to_date),
                    helperText: formik.touched.to_date && formik.errors.to_date,
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>

          {/* Poster — upload to /webinar/upload-poster, store the filename */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Poster
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {hasPoster ? (
                <Box sx={{ position: 'relative', width: 96, height: 72 }}>
                  <img
                    src={posterSrc}
                    alt="poster preview"
                    style={{
                      width: 96,
                      height: 72,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: '1px solid #e0e0e0',
                      // Dimmed while the upload is still in flight, so the
                      // preview reads as "not saved yet" rather than as done.
                      opacity: uploading ? 0.55 : 1,
                    }}
                  />

                  {/* Spinner over the image itself during the round trip. */}
                  {uploading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress size={22} />
                    </Box>
                  )}

                  <IconButton
                    size="small"
                    onClick={removePoster}
                    disabled={uploading}
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      bgcolor: 'error.main',
                      color: '#fff',
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 96,
                    height: 72,
                    borderRadius: 8,
                    border: '1px dashed #c4c4c4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                    fontSize: 12,
                  }}
                >
                  No poster
                </Box>
              )}

              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={uploading}
                startIcon={
                  uploading ? (
                    <CircularProgress size={16} />
                  ) : hasPoster ? (
                    <SwapHorizIcon fontSize="small" />
                  ) : (
                    <AttachFileIcon fontSize="small" />
                  )
                }
                sx={{ textTransform: 'none', borderStyle: 'dashed', borderRadius: 2 }}
              >
                {uploading ? 'Uploading...' : hasPoster ? 'Change Poster' : 'Upload Poster'}
                <input type="file" hidden accept="image/*" onChange={handlePosterUpload} />
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
              Images only, up to 4 MB.
            </Typography>
          </Box>

          {/*
            NO STATUS CONTROL IN EITHER MODE -- a note in its place instead.

            Activation is a single-slot operation (only one webinar may be
            active), so it belongs in the list where every webinar's status is
            visible at once and the one holding the slot can be seen. A toggle
            buried in this dialog could only ever be judged blind, and would
            fail at save time whenever a campaign was already running.

            The note is not decoration: without it, someone who has just filled
            in this form has no way of knowing where the status went.
          */}
          <Alert severity="info" sx={{ py: 0.5 }}>
            {row ? (
              <>
                This webinar&apos;s <strong>Active / Inactive</strong> status is unchanged by this
                form. Change it from the <strong>Status</strong> column in the list — only one
                webinar can be active at a time.
              </>
            ) : (
              <>
                This webinar will be created as <strong>Inactive</strong>. Activate it afterwards
                from the <strong>Status</strong> column in the list — only one webinar can be
                active at a time.
              </>
            )}
          </Alert>

          {mutating && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Stack direction="row" spacing={1}>
              <CancelButton action={handleCancel}>Cancel</CancelButton>
              {row ? (
                <UpdateButton disabled={mutating || uploading} type="submit">
                  {mutating ? 'Updating...' : 'Update'}
                </UpdateButton>
              ) : (
                <SubmitButton disabled={mutating || uploading} type="submit">
                  {mutating ? 'Submitting...' : 'Submit'}
                </SubmitButton>
              )}
            </Stack>
          </Box>
        </Stack>

        <ActiveConflictAlert
          open={conflict.open}
          message={conflict.message}
          conflictName={conflict.name}
          onClose={() => setConflict({ open: false, message: '', name: null })}
        />
      </Form>
    </FormikProvider>
  );
};

export default WebinarAddEdit;
